import { isLoggedInState } from "@/atoms/accountState";
import { Box } from "@chakra-ui/react";
import { css } from "@emotion/react";
import {
  ColorIcon,
  colors,
  customEvent,
  FUNERAL_SIGUNGU,
  FUNERAL_SIDO,
  HackleEventName,
  HStack,
  TwinButton,
  useResponsive,
  VStack,
  GoiLogo,
} from "goi_common";
import { useRouter } from "next/router";
import { ParlorDetailProps } from "@/types/parlor";
import { ReactNode, useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import { point, distance, featureCollection, nearestPoint } from "@turf/turf";

export default function ParlorCompareModal({
  isOpen,
  onClose,
  parlor,
  selectedSangjo,
  closestParlors,
}: {
  isOpen: boolean;
  onClose: () => void;
  parlor: ParlorDetailProps;
  selectedSangjo: string;
  closestParlors: any;
}) {
  const router = useRouter();
  const { setResponsive } = useResponsive();
  const isLoggedIn = useRecoilValue(isLoggedInState);

  return (
    <>
      {isOpen && (
        <VStack
          position="fixed"
          css={{ top: 0, left: 0 }}
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
          zIndex={1000}
        >
          <VStack
            background="white"
            maxWidth="400px"
            maxHeight="65%"
            width={setResponsive("90vw", "400px")}
            // height={setResponsive("calc(var(--vh, 1vh) * 92", "800px")}
            borderRadius="10px"
            position="relative"
            alignItems="center"
            className="body3_r"
            zIndex={9}
          >
            <VStack
              borderRadius="10px"
              width="100%"
              css={{
                overflowY: "scroll",
                [`::-webkit-scrollbar`]: {
                  display: "none",
                },
              }}
            >
              <ParlorModalContent
                key={parlor.id}
                closestParlors={closestParlors}
                parlor={parlor}
                onClose={onClose}
                selectedSangjo={selectedSangjo}
              />
            </VStack>
            <HStack width="100%" style={{ minHeight: "60px" }}>
              <TwinButton
                height="60px!important"
                firstButton={{
                  width: "50%",
                  background: colors.gray900,
                  text: `장례식장에서의 장례\n정말 저렴할까요?`,
                  onClick: () => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "compare_modal_ftr_lft-uv_about_parlor_funeral",
                      },
                    });
                    router.push("/guide/about-parlor-funeral/");
                  },
                }}
                secondButton={{
                  background: colors.orange650,
                  text: `${FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(
                    Number(parlor.sigungu),
                  )} 장례식장 비용\n전체 보기`,
                  onClick: () => {
                    customEvent({
                      newGtm: {
                        path: "parlor_detail",
                        trigger_and_target: "compare_modal_ftr_rght-uv_parlor_region",
                      },
                    });
                    if (isLoggedIn) {
                      router.push(
                        `/parlors/region/${FUNERAL_SIDO.get(
                          Number(parlor.sido),
                        )}-${FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(
                          Number(parlor.sigungu),
                        )}/`,
                      );
                    } else {
                      router.push(
                        `/login?redirectTo=${`/parlors/region/${FUNERAL_SIDO.get(
                          Number(parlor.sido),
                        )}-${FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(
                          Number(parlor.sigungu),
                        )}/`}`,
                      );
                    }
                  },
                }}
              />
            </HStack>
          </VStack>

          <VStack
            position="absolute"
            background="rgba(0,0,0,0.5)"
            width="100vw"
            height={`calc(var(--vh, 1vh) * 100)`}
            css={{ top: 0, left: 0 }}
            zIndex={8}
          ></VStack>
        </VStack>
      )}
    </>
  );
}

function getNearestDiscountParlor(origin: ParlorDetailProps) {
  const originPoint = point([origin.latitude, origin.longitude]);
  const points = featureCollection(
    parlors_discount
      .filter((p) => p.parlor_id !== Number(origin.id))
      .map((p) => point([p.latitude, p.longitude], { id: p.parlor_id })),
  );
  const nearest = nearestPoint(originPoint, points);
  if (distance(originPoint, nearest) <= 10) {
    return parlors_discount.find((p) => p.parlor_id === nearest.properties["id"]);
  } else {
    return;
  }
}

function ParlorModalContent({
  parlor,
  onClose,
  selectedSangjo,
  closestParlors,
}: {
  parlor: ParlorDetailProps;
  onClose: () => void;
  selectedSangjo: string;
  closestParlors: any;
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "scroll";
    };
  }, []);

  const router = useRouter();
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const isTeuer = useMemo(() => {
    const expectedCharges = parlor.expected_charge_sum;
    const avg = closestParlors.reduce((prev: any, curr: any) => prev + curr.price, 0) / 3;
    return expectedCharges[selectedSangjo] > avg;
  }, [closestParlors]);

  const updatedClosestParlors = useMemo(() => {
    return closestParlors;
  }, [closestParlors, parlor]);

  const nearestParlor = useMemo(() => {
    return getNearestDiscountParlor(parlor);
  }, [parlor]);
  return (
    <VStack width="100%">
      <VStack gap={16}>
        <VStack width="100%" padding="24px 24px 12px">
          <HStack
            width="100%"
            padding="0 0 6px"
            css={{ flexShrink: 0 }}
            borderRadius="10px"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <div
              className="subtitle2_b"
              css={{ wordBreak: "keep-all", display: "block", whiteSpace: "pre-line" }}
            >
              <span className="gray600">간편 로그인</span>으로 다른 장례식장의 상세 비용, 후기, 전체
              장례 비용을 알아볼 수 있습니다.
            </div>
            <VStack
              alignItems="center"
              justifyContent="center"
              margin="6px 0px 0px"
              css={{ cursor: "pointer" }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "parlor_detail",
                    trigger_and_target: "compare_modal-close_top",
                  },
                  hackle: { key: HackleEventName["장례식장 상세 푸터 - 모달 닫기"] },
                });
                onClose();
              }}
            >
              <ColorIcon name="icon-x-mono" size={20} color={colors.gray900} />
            </VStack>
          </HStack>
        </VStack>
      </VStack>
      <HStack
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        padding="16px 16px"
        background={colors.warmGray100}
        css={{
          cursor: "pointer",
        }}
      >
        <HStack justifyContent="space-between" width="100%" padding="0 12px">
          <div className="caption_12_b gray800">
            <span className="orange700">
              {parlor.companyname} {selectedSangjo}
            </span>{" "}
            비용,
            <br /> 주변 장례식장 평균 예상비용 보다{" "}
          </div>
          <Box width="110px">
            <ComparedPriceBadge isTeuer={isTeuer} />
          </Box>
        </HStack>
      </HStack>
      {nearestParlor && (
        <>
          <Box textAlign="center" className="body_14_m gray800" padding="16px 16px 8px">
            가까운 곳에 <span className="subtitle_14_b gray900">빈소이용료 할인</span>이 가능한
            장례식장이 있어요
          </Box>
          <Box margin="0 24px">
            <HStack
              width="100%"
              alignItems="center"
              justifyContent="space-between"
              padding="12px 16px"
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "parlor_detail",
                    trigger_and_target: "compare_modal-discount_other",
                  },
                });
                router.push(`/parlor/${nearestParlor.parlor_id}/`);
                onClose();
              }}
              background={colors.green50}
              css={{
                border: `1px solid ${colors.green300}`,
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              <HStack>
                <VStack justifyContent="center" width="100%" gap={3}>
                  <div className="subtitle_14_b gray800">{nearestParlor.memo}</div>
                  <div className="green500 subtitle_14_b">{nearestParlor.description} </div>
                </VStack>
              </HStack>
              <div>
                <ColorIcon
                  name="icon-arrow-right-small-mono"
                  width="36px"
                  color={colors.green400}
                />
              </div>
            </HStack>
            <span className="caption_12_b gray500">
              * <span className="gray800">고이 직영 상담시에 한하여</span> 혜택 제공 가능
            </span>
          </Box>
        </>
      )}
      <Box className="body_14_m" padding="24px 12px 6px">
        주변 장례식장 비용 정보
      </Box>
      <VStack
        position="relative"
        width="100%"
        background={colors.warmGray50}
        css={{
          borderTop: `1px solid ${colors.gray100}`,
        }}
      >
        {updatedClosestParlors.map((item: any, index: number) => {
          return (
            <ListItem
              key={item.name}
              isDiscount={Boolean(parlors_discount.find((p) => p.parlor_id === Number(item.id)))}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "parlor_detail",
                    trigger_and_target: "compare_modal_others-uv_parlor_detail",
                  },
                  hackle: {
                    key: HackleEventName["장례식장 상세 푸터 - 다른 장례식장 클릭"],
                    properties: {
                      postion: index,
                      name: item.name,
                      ditance: item.distance,
                      isMoreExpensive: item.isTeuer,
                    },
                  },
                });
                if (isLoggedIn) {
                  router
                    .push(`/parlor/${item.id}/`)
                    .then()
                    .catch()
                    .finally(() => {
                      onClose();
                    });
                } else {
                  router
                    .push(`/login?redirectTo=/parlor/${item.id}/`)
                    .then()
                    .catch()
                    .finally(() => {
                      onClose();
                    });
                }
              }}
              icon={
                <ColorIcon
                  name={
                    item.isTeuer > 0
                      ? "arrow_fat_up_fill"
                      : item.isTeuer < 0
                      ? "arrow_fat_down_fill"
                      : "menu_dots"
                  }
                  color={
                    item.isTeuer > 0
                      ? colors.danger
                      : item.isTeuer < 0
                      ? colors.success
                      : colors.warmGray700
                  }
                  size={32}
                />
              }
              title={item.name}
              text={
                <HStack gap={3} alignItems="center">
                  <ColorIcon name="location" color={colors.gray800} width="14px" />
                  <span className="gray700 caption1">{item.distance}km &nbsp;&nbsp;</span>

                  <PriceBadge isTeuer={item.isTeuer} />
                </HStack>
              }
            />
          );
        })}
      </VStack>
      <Box className="caption1" padding="12px 16px">
        <span className="gray500">
          상세 비용은 공제 품목, 희망 사항 등에 따라 변경될 수 있습니다.
        </span>
      </Box>
    </VStack>
  );
}

function ListItem({
  icon,
  title,
  text,
  onClick,
  isDiscount,
}: {
  icon: ReactNode;
  title: ReactNode;
  text: ReactNode;
  onClick: () => void;
  isDiscount?: boolean;
}) {
  return (
    <HStack
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      padding="12px 16px"
      onClick={onClick}
      css={{
        borderBottom: `1px solid ${colors.gray100}`,
        cursor: "pointer",
      }}
    >
      <HStack>
        <VStack
          css={{ flexShrink: 0, marginRight: 10 }}
          justifyContent="center"
          alignItems="center"
        >
          {icon}
        </VStack>
        <VStack justifyContent="center" width="100%" gap={3}>
          {isDiscount && (
            <Box
              background="rgba(0, 0, 0, 0.5)"
              backdropFilter="blur(2px)"
              borderRadius="18px"
              padding="2px 10px 2px 2px"
            >
              <HStack gap="4px" alignItems="center" justifyContent="center">
                <Box margin="auto" background={colors.gray900} padding="4px" borderRadius="50%">
                  <ColorIcon
                    name="icon-medal-check-mono"
                    color={colors.reviewYellow}
                    width={16}
                    height={16}
                  />
                </Box>
                <HStack gap="6px" alignItems="center" justifyContent="center">
                  <GoiLogo color={colors.white} size={16} />
                  <span className="button_12_sb white">빈소 할인 제휴 장례식장</span>
                </HStack>
              </HStack>
            </Box>
          )}
          <div className="body3_r gray800">{title}</div>
          <div className="caption1 gray600">{text}</div>
        </VStack>
      </HStack>
      <div>
        <ColorIcon name="icon-arrow-right-small-mono" width="36px" color={colors.gray400} />
      </div>
    </HStack>
  );
}

function PriceBadge({ isTeuer }: { isTeuer: number }) {
  return (
    <HStack alignItems="center" className="caption1" gap={4} css={bedgeCss}>
      <div>{isTeuer > 0 ? "더 비싸요" : isTeuer < 0 ? "더 저렴해요" : "비슷해요"}</div>
    </HStack>
  );
}

const bedgeCss = css({
  backgroundColor: colors.warmGray150,
  padding: "3px 12px",
  borderRadius: "40px",
});

const ComparedPriceBadge = ({ isTeuer }: { isTeuer: boolean }) => {
  const theme = useMemo(() => {
    if (!isTeuer) {
      return {
        backgroundColor: colors.green50,
        borderColor: colors.green100,
        color: "green450",
        iconColor: colors.success,
      };
    } else {
      return {
        backgroundColor: colors.orange100,
        borderColor: colors.orange400,
        color: "orange700",
        iconColor: colors.danger,
      };
    }
  }, [isTeuer]);
  return (
    <div
      style={{
        margin: "auto",
        border: `1px solid ${theme.borderColor}`,
        borderRadius: "5px",
        width: "fit-content",
      }}
    >
      <HStack margin="0 0 1px" background={theme.backgroundColor} padding="4px 12px" gap="4px">
        <ColorIcon
          name={isTeuer ? "arrow_fat_up_fill" : "arrow_fat_down_fill"}
          color={theme.iconColor}
          width="20px"
          height="20px"
        />
        <span className={`caption_12_sb ${theme.color}`}>더 {isTeuer ? "비싸요" : "저렴해요"}</span>
      </HStack>
    </div>
  );
};
